import DataTable from "../../components/common/DataTable";
import React from "react";
import { Progress } from "@mantine/core";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { notifications } from "@mantine/notifications";
import { AnalyticsPageLayout, StatCard, ChartCard, TOOLTIP_STYLE, chartProps } from "./AnalyticsLayout";
import { Smartphone } from "lucide-react";

const deviceStats = [
  { name: "Mobile", value: 55, color: "#3b82f6" },
  { name: "Tablet", value: 20, color: "#10b981" },
  { name: "Desktop", value: 18, color: "#f59e0b" },
  { name: "Smart TV", value: 7, color: "#8b5cf6" },
];

const osStats = [
  { os: "iOS", users: 38000, sessions: 125400, avgTime: "42 mins" },
  { os: "Android", users: 52000, sessions: 156800, avgTime: "38 mins" },
  { os: "Windows", users: 18000, sessions: 48900, avgTime: "35 mins" },
  { os: "Mac", users: 12000, sessions: 31200, avgTime: "41 mins" },
  { os: "tvOS", users: 8000, sessions: 18400, avgTime: "58 mins" },
];

const deviceAnalytics = [
  { key: "1", device: "iPhone 14 Pro", users: 18500, activeToday: 12300, avgSessionDuration: "45 mins", crashRate: 0.02, engagement: 92 },
  { key: "2", device: "Samsung Galaxy S23", users: 22100, activeToday: 14200, avgSessionDuration: "38 mins", crashRate: 0.05, engagement: 88 },
  { key: "3", device: "iPad Pro", users: 14800, activeToday: 8900, avgSessionDuration: "54 mins", crashRate: 0.01, engagement: 95 },
  { key: "4", device: "Windows PC", users: 18000, activeToday: 9200, avgSessionDuration: "35 mins", crashRate: 0.03, engagement: 82 },
];

const deviceColumns = [
  { title: "Device", dataIndex: "device", key: "device" },
  { title: "Total Users", dataIndex: "users", key: "users", render: (t) => t.toLocaleString() },
  { title: "Active Today", dataIndex: "activeToday", key: "activeToday", render: (t) => t.toLocaleString() },
  { title: "Avg Session", dataIndex: "avgSessionDuration", key: "avgSessionDuration" },
  { title: "Crash Rate", dataIndex: "crashRate", key: "crashRate", render: (t) => `${(t * 100).toFixed(2)}%` },
  {
    title: "Engagement",
    dataIndex: "engagement",
    key: "engagement",
    render: (v) => <Progress value={v} color="green" size="sm" style={{ width: 60 }} />,
  },
];

const deviceTrendData = [
  { week: "Week 1", mobile: 85000, tablet: 32000, desktop: 28000, tv: 10000 },
  { week: "Week 2", mobile: 92000, tablet: 35000, desktop: 30000, tv: 11000 },
  { week: "Week 3", mobile: 98000, tablet: 38000, desktop: 32000, tv: 12000 },
  { week: "Week 4", mobile: 105000, tablet: 41000, desktop: 35000, tv: 13000 },
];

const DeviceAnalyticsPage = () => {
  const handleExport = (format) => {
    notifications.show({ message: `Exporting as ${format.toUpperCase()}...`, color: "green" });
  };

  return (
    <AnalyticsPageLayout
      icon={Smartphone}
      title="Device Analytics"
      subtitle="Device types & operating system performance"
      onExport={handleExport}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Total Devices" value={132400} suffix="" />
        <StatCard title="Active Today" value={62100} suffix="" />
        <StatCard title="Avg Session" value={42} suffix=" mins" />
        <StatCard title="Avg Crash Rate" value={0.03} suffix="%" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartCard title="Device Type Distribution">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={deviceStats} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name} ${value}%`} labelLine={false}>
                  {deviceStats.map((e, i) => <Cell key={i} fill={e.color} stroke="#0a0a0a" strokeWidth={2} />)}
                </Pie>
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} formatter={(v, n, p) => [`${v}%`, p.payload.name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
        <ChartCard title="Device Type Growth">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={deviceTrendData}>
                <CartesianGrid {...chartProps.grid} />
                <XAxis dataKey="week" {...chartProps.axis} />
                <YAxis {...chartProps.axis} />
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="mobile" fill="#3b82f6" name="Mobile" />
                <Bar dataKey="tablet" fill="#10b981" name="Tablet" />
                <Bar dataKey="desktop" fill="#f59e0b" name="Desktop" />
                <Bar dataKey="tv" fill="#8b5cf6" name="Smart TV" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <div className="mb-8">
      <ChartCard title="Operating System Performance">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {osStats.map((s, i) => (
            <div key={i} className="exec-stat-card p-4 rounded-lg text-center">
              <div className="text-zinc-500 text-xs font-medium mb-2">{s.os}</div>
              <div className="text-white text-lg font-bold">{s.users.toLocaleString()}</div>
              <div className="text-zinc-400 text-xs mt-1">Sessions: {s.sessions.toLocaleString()}</div>
              <div className="text-blue-400 text-xs mt-1">Avg: {s.avgTime}</div>
            </div>
          ))}
        </div>
      </ChartCard>
      </div>

      <ChartCard title="Top Devices Performance">
        <DataTable dataSource={deviceAnalytics} columns={deviceColumns} pagination={false} scroll={{ x: 1000 }} rowKey="key" />
      </ChartCard>
    </AnalyticsPageLayout>
  );
};

export default DeviceAnalyticsPage;
